import React from 'react';
import styles from './GameEndPopup.module.css';
import { useLanguage } from '../Providers/LanguageContext';
import { PieceType } from '../../services/zodiacEngine/types';

export type WinCondition = 'kingslayer' | 'race' | 'timeout' | 'threefold' | 'no_moves' | 'draw';

interface GameEndPopupProps {
  isVisible: boolean;
  result: 'won' | 'lost' | 'draw';
  condition: WinCondition;
  raceKingType?: string; // The type of king that won by race (e.g., "Wolf")
  capturedKingType?: PieceType; // The PieceType of the king captured in a kingslayer ending
  capturedKingIsLight?: boolean; // Side of the captured king (for icon color)
  onClose: () => void;
  onRematch: () => void;
  onBackToMenu: () => void;
}

// Map condition keys to translation keys
const CONDITION_TRANSLATION_KEYS: Record<WinCondition, string> = {
  kingslayer: 'gameEnd.conditions.kingslayer',
  race: 'gameEnd.conditions.race',
  timeout: 'gameEnd.conditions.timeout',
  threefold: 'gameEnd.conditions.threefold',
  no_moves: 'gameEnd.conditions.noMoves',
  draw: 'gameEnd.conditions.draw'
};

function GameEndPopup({
  isVisible,
  result,
  condition,
  raceKingType,
  capturedKingType,
  capturedKingIsLight,
  onClose,
  onRematch,
  onBackToMenu
}: GameEndPopupProps) {
  const { t } = useLanguage();

  if (!isVisible) return null;

  const isVictory = result === 'won';
  const isDraw = result === 'draw';

  const getTitle = () => {
    if (isDraw) return t('gameEnd.draw');
    if (isVictory) return t('gameEnd.victory');
    return t('gameEnd.defeat');
  };

  const getMessage = () => {
    // Special handling for race victories - show the king type
    if (condition === 'race' && raceKingType) {
      if (isVictory) {
        return `You raced your ${raceKingType} King across the board`;
      } else {
        return `Opponent raced their ${raceKingType} King across the board`;
      }
    }

    const conditionLabel = t(CONDITION_TRANSLATION_KEYS[condition]);
    if (isDraw) return t('gameEnd.drawBy', { condition: conditionLabel });
    if (isVictory) return t('gameEnd.wonBy', { condition: conditionLabel });
    return t('gameEnd.lostBy', { condition: conditionLabel });
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={`${styles.popup} ${
        isDraw ? styles.draw : isVictory ? styles.victory : styles.defeat
      }`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{getTitle()}</h2>
          <div className={styles.icon}>
            {isDraw ? (
              <div className={styles.drawIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                  <path d="M12 3C10.34 3 9 4.34 9 6c0 1.1.6 2.05 1.5 2.55v2.95l-4.03 4.03c-.3.3-.47.71-.47 1.13V19h12v-2.34c0-.42-.17-.83-.47-1.13L13.5 11.5V8.55c.9-.5 1.5-1.45 1.5-2.55 0-1.66-1.34-3-3-3z"/>
                </svg>
              </div>
            ) : isVictory ? (
              <div className={styles.victoryIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                </svg>
              </div>
            ) : (
              <div className={styles.defeatIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"/>
                </svg>
              </div>
            )}
          </div>
        </div>

        <div className={styles.content}>
          <p className={styles.conditionMessage}>{getMessage()}</p>
          {condition === 'kingslayer' && capturedKingType && capturedKingIsLight !== undefined && !isDraw && (
            <div className={styles.capturedKingSection}>
              <img
                src={`/Icons/${capturedKingType.toLowerCase()}-${capturedKingIsLight ? 'light' : 'dark'}.png`}
                alt={capturedKingType}
                className={styles.capturedKingIcon}
              />
              <p className={styles.capturedKingText}>
                {isVictory
                  ? t('gameEnd.youCapturedTheirKing', { piece: formatPieceName(capturedKingType) })
                  : t('gameEnd.yourKingWasCaptured', { piece: formatPieceName(capturedKingType) })}
              </p>
            </div>
          )}

          <div className={styles.actionButtons}>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.rematchButton}`}
              onClick={onRematch}
            >
              {t('gameEnd.rematchButton')}
            </button>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.backButton}`}
              onClick={onBackToMenu}
            >
              {t('gameEnd.backButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatPieceName(pieceType: PieceType): string {
  return pieceType.charAt(0).toUpperCase() + pieceType.slice(1).toLowerCase();
}

export default GameEndPopup;
